import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	type ColumnFiltersState,
	type ColumnSizingState,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	type Row,
	useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import Fuse from "fuse.js";
import {
	AlertTriangle,
	Filter,
	Plus,
	RotateCcw,
	Send,
	Trash2,
	Undo2,
	X,
} from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	type AutoCombineEntryV2,
	buildRunningTimerRow,
	type EntryGroupWire,
	type EntryMatter,
	type EntryMiniTransaction,
	type EntryStatus,
	entriesAutoCombineQueryKey,
	entryGroupsQueryKey,
	entryGroupToEntryGroupEntry,
	hasManualNarrative,
	hasStaleNarrative,
	useEntriesAutoCombineCache,
	useEntryFiltersCache,
	useMattersCache,
	useSettingsCache,
} from "@/shared/cache";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useScopedRowSelectionState } from "@/shared/hooks/useScopedRowSelectionState";
import type { RequestOptions } from "@/shared/types/request";
import { useApi } from "@/shared/utils/request";
import {
	DatePicker,
	DatePickerDefaultFooter,
} from "@/v2/components/date-picker/DatePicker";
import { MatterLockButton } from "@/v2/components/MatterLockButton";
import { Badge } from "@/v2/components/ui/badge";
import { Button } from "@/v2/components/ui/button";
import { ButtonGroup } from "@/v2/components/ui/button-group";
import { TooltipProvider } from "@/v2/components/ui/tooltip";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import type { QueuedAction } from "@/v2/hooks/actions/actionTypes";
import { EntryMutationOutcomeError } from "@/v2/hooks/actions/actionTypes";
import {
	type AutoCombineActionDeps,
	addAutoCombineEntryGroupAction,
	createEntryGroupFromSourceEntriesAction,
	deleteAutoCombineAction,
	deleteAutoCombineSelectionAction,
	duplicateAutoCombineEntryGroupAction,
	editAutoCombineEntryGroupAction,
	editAutoCombineSourceEntryAction,
	exportAutoCombineAction,
	firstEntryGroupResponse,
	markAutoCombineStatusAction,
	moveMiniTransactionsAction,
	restoreAutoCombineAction,
} from "@/v2/hooks/actions/autoCombine";
import { durationToUnitsSeconds } from "@/v2/hooks/actions/entryId";
import { actingHeaders } from "@/v2/hooks/actions/types";
import { useSmartTimer } from "@/v2/hooks/useSmartTimer";
import { isCompleteDateRange } from "@/v2/lib/dates";
import {
	resolveNewEntryGroupFields,
	uniqueSelectedParents,
	uniqueSelectedSourceIds,
} from "@/v2/lib/entryGroupSelection";
import { cn } from "@/v2/lib/utils";
import { AddEntryDialog } from "@/v2/pages/Entries/components/AddEntryDialog";
import { EditEntryDialog } from "@/v2/pages/Entries/components/EditEntryDialog";
import { useEntriesUndo } from "@/v2/undo";
import { AutoCombineTable, type MoveMode } from "./AutoCombineTable";
import styles from "./AutoCombineView.module.css";
import {
	AUTO_COLUMN_LABELS,
	AUTO_DEFAULT_COLUMN_ORDER,
	AUTO_DEFAULT_COLUMN_VISIBILITY,
	autoColumnOrderParser,
	autoVisibilityParser,
	getAutoColumns,
	TIMER_COLUMN_ID,
} from "./autoCombineColumns";
import { computeDateFilter, makeEntryPredicate } from "./entryFilter";
import { useCanonicalTimekeeperScope } from "./filters/canonical";
import { isDefaultFilters } from "./filters/defaults";
import { FilterMenu } from "./filters/FilterMenu";
import { useFilterOptions } from "./filters/useFilterOptions";
import { useFilters } from "./filters/useFilters";
import { SourceEntryEditDialog } from "./SourceEntryEditDialog";
import { StatsBar } from "./StatsBar";
import { StatusIcon } from "./StatusIcon";
import { AUTO_FUSE_OPTIONS } from "./search/fuseConfig";
import { SearchBar } from "./search/SearchBar";
import { ToolbarTimerButton } from "./ToolbarTimerButton";
import { useConfiguredTimekeepers } from "./timekeepers";
import { ColumnsMenu } from "./toolbar/ColumnsMenu";
import { IconButton } from "./toolbar/IconButton";
import { useMiniTransactionSelection } from "./useMiniTransactionSelection";
import {
	readColumnPreferenceDefaults,
	usePersistedColumnPreferences,
} from "./usePersistedColumnPreferences";
import { DEFAULT_SORTING, sortingParser } from "./viewParams";

type PendingAutoCombineAction = QueuedAction & {
	pendingIds?: string[];
	narrativePendingIds?: string[];
};
type PendingAutoCombineContext = {
	action: PendingAutoCombineAction;
	toastId?: string | number;
	rollback?: (() => void) | null;
};

const NARRATIVE_STREAM_INTERVAL_MS = 50;
const AUTO_COLUMN_PREFS_STORAGE_KEY = "entries:auto-combine:columns:v1";
const ENTRY_GROUP_ID_PATTERN = /\/v2\/entry-groups\/(eg_\d+)(?:\/|$)/;
const ENTRY_ID_PATTERN = /\/v2\/entries\/(ent_\d+)(?:\/|$)/;

type NarrativeStreamTimer = {
	intervalId: ReturnType<typeof setInterval>;
	cleanupTimeoutId?: ReturnType<typeof setTimeout>;
	clearOnComplete: boolean;
};

function hasActingHeader(headers?: Record<string, string>): boolean {
	if (!headers) return false;
	return Object.keys(headers).some((key) => {
		const normalized = key.toLowerCase();
		return normalized === "x-acting-for" || normalized === "x-act-as-prof-id";
	});
}

function addStringIds(value: unknown, output: Set<string>): void {
	if (!Array.isArray(value)) return;
	for (const item of value) {
		if (typeof item === "string") output.add(item);
	}
}

function getRequestObject(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function requestEntryGroupIds(opts: RequestOptions): Set<string> {
	const ids = new Set<string>();
	const endpointMatch = opts.endpoint.match(ENTRY_GROUP_ID_PATTERN);
	if (endpointMatch) ids.add(endpointMatch[1]);

	const body = getRequestObject(opts.body);
	addStringIds(body?.entryGroupIds, ids);
	addStringIds(body?.sourceEntryGroupIds, ids);
	if (typeof body?.primaryEntryGroupId === "string")
		ids.add(body.primaryEntryGroupId);

	if (Array.isArray(body?.data)) {
		for (const item of body.data) {
			const row = getRequestObject(item);
			if (!row) continue;
			if (typeof row.id === "string") ids.add(row.id);
			addStringIds(row.sourceEntryGroupIds, ids);
			if (typeof row.primaryEntryGroupId === "string")
				ids.add(row.primaryEntryGroupId);
			const duplicate = getRequestObject(row.duplicate);
			addStringIds(duplicate?.entryGroupIds, ids);
		}
	}

	return ids;
}

function requestEntryIds(opts: RequestOptions): Set<string> {
	const ids = new Set<string>();
	const endpointMatch = opts.endpoint.match(ENTRY_ID_PATTERN);
	if (endpointMatch) ids.add(endpointMatch[1]);

	const body = getRequestObject(opts.body);
	addStringIds(body?.entryIds, ids);
	addStringIds(body?.addEntryIds, ids);
	addStringIds(body?.removeEntryIds, ids);

	if (Array.isArray(body?.data)) {
		for (const item of body.data) {
			const row = getRequestObject(item);
			if (!row) continue;
			if (typeof row.id === "string") ids.add(row.id);
			addStringIds(row.entryIds, ids);
			const duplicate = getRequestObject(row.duplicate);
			addStringIds(duplicate?.entryIds, ids);
		}
	}

	return ids;
}

function uniqueTimekeeperIdForRequest(
	opts: RequestOptions,
	entries: AutoCombineEntryV2[],
): string | null | false {
	const entryGroupIds = requestEntryGroupIds(opts);
	const entryIds = requestEntryIds(opts);
	if (entryGroupIds.size === 0 && entryIds.size === 0) return null;

	const owners = new Set<string>();
	for (const entry of entries) {
		const ownsGroup = entryGroupIds.has(entry.id);
		const ownsSource = entry.miniTransactions.some((mini) =>
			entryIds.has(mini.id),
		);
		if (ownsGroup || ownsSource) owners.add(entry.timekeeperId ?? "");
	}

	if (owners.size === 0) return null;
	if (owners.size > 1) return false;
	const [owner] = owners;
	return owner || null;
}

function withActingHeaders(
	opts: RequestOptions,
	timekeeperId: string | null | undefined,
): RequestOptions {
	if (!timekeeperId || hasActingHeader(opts.headers)) return opts;
	return {
		...opts,
		headers: {
			...actingHeaders(timekeeperId),
			...(opts.headers ?? {}),
		},
	};
}

function withAutoCombineActingHeaders(
	opts: RequestOptions,
	entries: AutoCombineEntryV2[],
	fallbackTimekeeperId: string | null,
): RequestOptions {
	if (hasActingHeader(opts.headers)) return opts;

	const ownerTimekeeperId = uniqueTimekeeperIdForRequest(opts, entries);
	if (ownerTimekeeperId === false) return opts;

	return withActingHeaders(opts, ownerTimekeeperId ?? fallbackTimekeeperId);
}

export default function AutoCombineView() {
	const [columnPreferenceDefaults] = useState(() =>
		readColumnPreferenceDefaults({
			storageKey: AUTO_COLUMN_PREFS_STORAGE_KEY,
			visibilityParam: "autoCols",
			orderParam: "autoColOrder",
			defaultVisibility: AUTO_DEFAULT_COLUMN_VISIBILITY,
			defaultOrder: AUTO_DEFAULT_COLUMN_ORDER,
			visibilityParser: autoVisibilityParser,
			orderParser: autoColumnOrderParser,
		}),
	);
	const [sorting, setSorting] = useQueryState(
		"sort",
		sortingParser
			.withDefault(DEFAULT_SORTING)
			.withOptions({ history: "replace" }),
	);
	const [columnVisibility, setColumnVisibility] = useQueryState(
		"autoCols",
		autoVisibilityParser
			.withDefault(columnPreferenceDefaults.visibility)
			.withOptions({ history: "replace" }),
	);
	const [columnOrder, setColumnOrder] = useQueryState(
		"autoColOrder",
		autoColumnOrderParser
			.withDefault(columnPreferenceDefaults.order)
			.withOptions({ history: "replace" }),
	);
	const effectiveColumnVisibility = useMemo(
		() => ({ ...columnVisibility, application: false }),
		[columnVisibility],
	);
	usePersistedColumnPreferences({
		storageKey: AUTO_COLUMN_PREFS_STORAGE_KEY,
		visibility: effectiveColumnVisibility,
		order: columnOrder,
		visibilityParser: autoVisibilityParser,
		orderParser: autoColumnOrderParser,
	});
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

	const [qParam, setQParam] = useQueryState(
		"q",
		parseAsString.withDefault("").withOptions({ history: "replace" }),
	);
	const [searchQuery, setSearchQuery] = useState(qParam);
	const debouncedQuery = useDebounce(searchQuery, 150);
	useEffect(() => {
		setQParam(debouncedQuery === "" ? null : debouncedQuery);
	}, [debouncedQuery, setQParam]);

	const { date, dateRange, setDate, setDateRange } = useDateParams();
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const { entryFilters } = useEntryFiltersCache();
	const timekeepers = useConfiguredTimekeepers();
	const tableColumns = useMemo(
		() => getAutoColumns(timekeepers),
		[timekeepers],
	);
	const columnMenuLabels = useMemo(() => {
		if (timekeepers) return AUTO_COLUMN_LABELS;
		return Object.fromEntries(
			Object.entries(AUTO_COLUMN_LABELS).filter(([id]) => id !== "timekeeper"),
		);
	}, [timekeepers]);
	const defaultColumnOrder = useMemo(
		() =>
			timekeepers
				? AUTO_DEFAULT_COLUMN_ORDER
				: AUTO_DEFAULT_COLUMN_ORDER.filter((id) => id !== "timekeeper"),
		[timekeepers],
	);
	const { canFetchEntries, requestTimekeeperId: timekeeperRequestScope } =
		useCanonicalTimekeeperScope();
	const { entries: rawData, isLoading: entriesLoading } =
		useEntriesAutoCombineCache(matters, !mattersLoading && canFetchEntries, {
			timekeeperId: timekeeperRequestScope,
		});
	const { activeEntryGroupId, pausingEntryGroupId } = useSmartTimer();
	// The group the timer is running on (kept through the in-flight pause so the
	// live row doesn't flicker while it winds down).
	const runningId = activeEntryGroupId ?? pausingEntryGroupId;
	// A smart timer can create an empty `tracked` group before any work lands in
	// it. Normally we hide empty tracked groups, but the *running* group stays
	// visible with a live-ticking Units cell so the timer shows in real time.
	const data = useMemo(() => {
		const today = format(new Date(), "yyyy-MM-dd");
		// A temporary end-time floats the running row to the top of the time-sorted
		// table (it's the sort key), so the active timer is where the eye lands
		// instead of buried at the bottom.
		const nowIso = new Date().toISOString();
		const kept: AutoCombineEntryV2[] = [];
		let hasRunning = false;
		for (const group of rawData) {
			const isRunning = group.id === runningId;
			if (isRunning) hasRunning = true;
			if (
				group.miniTransactions.length > 0 ||
				group.status !== "tracked" ||
				isRunning
			) {
				if (isRunning && group.miniTransactions.length === 0) {
					// Empty running group: pre-populate as a .1 timer entry, anchored to
					// today and floated to the top; the Units cell ticks up from here.
					kept.push({
						...group,
						date: group.date || today,
						lastEntryAt: nowIso,
						units: 0.1,
						unitsSeconds: 360,
					});
				} else if (isRunning) {
					// Running group that already captured work: keep its real units,
					// just float it to the top.
					kept.push({ ...group, lastEntryAt: nowIso });
				} else {
					kept.push(group);
				}
			}
		}
		// Instant feedback: if the just-started group hasn't returned from the
		// entry-groups refetch yet, show a synthetic live row until it does.
		if (runningId && !hasRunning) {
			kept.unshift({
				...buildRunningTimerRow(runningId, today),
				lastEntryAt: nowIso,
			});
		}
		return kept;
	}, [rawData, runningId]);
	// The empty running placeholder isn't a real editable/exportable row yet.
	const runningPlaceholderId = useMemo(() => {
		if (!runningId) return null;
		const row = data.find((entry) => entry.id === runningId);
		return row && row.miniTransactions.length === 0 ? runningId : null;
	}, [data, runningId]);
	const isLoading = mattersLoading || !canFetchEntries || entriesLoading;
	const { settings, updateSettings } = useSettingsCache();
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();
	const refreshAutoCombine = useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: entriesAutoCombineQueryKey }),
		[queryClient],
	);
	const refreshEntryGroups = useCallback(
		() => queryClient.invalidateQueries({ queryKey: entryGroupsQueryKey }),
		[queryClient],
	);
	const makeScopedApiRequest = useCallback(
		(opts: RequestOptions) =>
			makeApiRequest(
				withAutoCombineActingHeaders(opts, data, timekeeperRequestScope),
			),
		[makeApiRequest, data, timekeeperRequestScope],
	);

	const fieldOptions = useFilterOptions(data, timekeepers);
	const {
		filters,
		defaultFilters,
		toggleFieldValue: toggleFilterFieldValue,
		selectAll: selectAllFilterValues,
		selectNone: selectNoFilterValues,
		reset: resetFilters,
	} = useFilters(fieldOptions);

	// Search reaches into source-entry summaries/apps (see AUTO_FUSE_OPTIONS). Built
	// lazily, same as normal mode.
	const trimmedQuery = debouncedQuery.trim();
	const hasSearchQuery = trimmedQuery.length > 0;
	const fuse = useMemo(
		() => (hasSearchQuery ? new Fuse(data, AUTO_FUSE_OPTIONS) : null),
		[data, hasSearchQuery],
	);
	const searchHits = useMemo(() => {
		if (!fuse || !trimmedQuery) return null;
		return new Set(fuse.search(trimmedQuery).map((r) => r.item.id));
	}, [fuse, trimmedQuery]);

	const dateFilter = useMemo(
		() => computeDateFilter(date, dateRange),
		[date, dateRange],
	);

	const columnFilters = useMemo<ColumnFiltersState>(() => {
		const out: ColumnFiltersState = [{ id: "date", value: dateFilter }];
		out.push({ id: "status", value: { values: filters.status } });
		if (filters.timekeeper) {
			out.push({ id: "timekeeper", value: { values: [filters.timekeeper] } });
		}
		for (const field of [
			"application",
			"matter",
			"client",
			"billableCategory",
			"task",
			"activity",
		] as const) {
			const values = filters[field];
			if (values !== undefined) out.push({ id: field, value: { values } });
		}
		return out;
	}, [dateFilter, filters]);

	// ── Selection (entry groups + source entries can coexist) ──
	const selectionScopeKey = useMemo(
		() =>
			[
				dateFilter.from ?? "",
				dateFilter.to ?? "",
				debouncedQuery.trim(),
				JSON.stringify(filters),
			].join("|"),
		[dateFilter, debouncedQuery, filters],
	);
	const { rowSelection, handleRowSelectionChange, clearSelection } =
		useScopedRowSelectionState(selectionScopeKey, {
			restoreOnScopeReturn: false,
		});

	const { selectedMiniIds, toggleMini, clearMiniSelection } =
		useMiniTransactionSelection(data);

	const handleToggleMini = useCallback(
		(id: string) => {
			toggleMini(id);
		},
		[toggleMini],
	);

	const clearMixedSelection = useCallback(() => {
		clearSelection();
		clearMiniSelection();
	}, [clearSelection, clearMiniSelection]);

	const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
	const toggleExpand = useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);
	const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...columnOrder,
			TIMER_COLUMN_ID,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[columnOrder],
	);

	const table = useReactTable({
		data,
		columns: tableColumns,
		state: {
			sorting,
			columnVisibility: effectiveColumnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection,
			columnFilters,
			columnSizing,
			globalFilter: searchHits,
		},
		onSortingChange: setSorting,
		onColumnVisibilityChange: setColumnVisibility,
		onColumnOrderChange: setColumnOrder,
		onRowSelectionChange: handleRowSelectionChange,
		onColumnSizingChange: setColumnSizing,
		enableRowSelection: (row) => row.original.id !== runningPlaceholderId,
		enableColumnResizing: true,
		columnResizeMode: "onEnd",
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: (row: Row<AutoCombineEntryV2>, _id, value) =>
			value == null || (value as Set<string>).has(row.original.id),
	});

	const expandAll = useCallback(
		() =>
			setExpandedIds(
				new Set(table.getRowModel().rows.map((r) => r.original.id)),
			),
		[table],
	);

	const selectedEntries = useMemo(() => {
		const predicate = makeEntryPredicate(filters, dateFilter);
		return data.filter(
			(e) =>
				rowSelection[e.id] &&
				predicate(e) &&
				(searchHits === null || searchHits.has(e.id)),
		);
	}, [data, filters, dateFilter, rowSelection, searchHits]);
	const selectedCount = selectedEntries.length;
	const selectedGroupIds = useMemo(
		() => new Set(selectedEntries.map((entry) => entry.id)),
		[selectedEntries],
	);
	const miniParentById = useMemo(() => {
		const map = new Map<string, AutoCombineEntryV2>();
		for (const entry of data) {
			for (const mini of entry.miniTransactions) {
				map.set(mini.id, entry);
			}
		}
		return map;
	}, [data]);
	const selectedLooseMiniIds = useMemo(() => {
		const ids: string[] = [];
		for (const id of selectedMiniIds) {
			const parent = miniParentById.get(id);
			if (!parent || !selectedGroupIds.has(parent.id)) ids.push(id);
		}
		return ids;
	}, [miniParentById, selectedGroupIds, selectedMiniIds]);
	const selectedLooseMiniParentIds = useMemo(() => {
		const ids = new Set<string>();
		for (const id of selectedLooseMiniIds) {
			const parent = miniParentById.get(id);
			if (parent) ids.add(parent.id);
		}
		return ids;
	}, [miniParentById, selectedLooseMiniIds]);
	const selectedGroupMiniIds = useMemo(
		() =>
			selectedEntries.flatMap((entry) =>
				entry.miniTransactions.map((mini) => mini.id),
			),
		[selectedEntries],
	);
	const expandedSelectedMiniIds = useMemo(
		() => [...new Set([...selectedGroupMiniIds, ...selectedLooseMiniIds])],
		[selectedGroupMiniIds, selectedLooseMiniIds],
	);
	const expandedSelectedParentIds = useMemo(
		() => new Set([...selectedGroupIds, ...selectedLooseMiniParentIds]),
		[selectedGroupIds, selectedLooseMiniParentIds],
	);
	const expandedSelectedParents = useMemo(
		() => data.filter((entry) => expandedSelectedParentIds.has(entry.id)),
		[data, expandedSelectedParentIds],
	);
	const selectedSourceEntries = useMemo(() => {
		const sources = new Map<
			string,
			{ parent: AutoCombineEntryV2; mini: EntryMiniTransaction }
		>();
		for (const parent of data) {
			for (const mini of parent.miniTransactions) {
				sources.set(mini.id, { parent, mini });
			}
		}
		return expandedSelectedMiniIds
			.map((id) => sources.get(id))
			.filter(
				(
					source,
				): source is {
					parent: AutoCombineEntryV2;
					mini: EntryMiniTransaction;
				} => Boolean(source),
			);
	}, [data, expandedSelectedMiniIds]);
	const selectedSourceParents = useMemo(
		() => uniqueSelectedParents(selectedSourceEntries),
		[selectedSourceEntries],
	);
	const selectedSourceOwnerCount = useMemo(
		() =>
			new Set(
				selectedSourceParents.map(
					(parent) => parent.timekeeperId ?? "__current__",
				),
			).size,
		[selectedSourceParents],
	);
	const selectionCoversSingleParent =
		selectedSourceParents.length === 1 &&
		selectedSourceEntries.length ===
			selectedSourceParents[0].miniTransactions.length;
	const selectionHasExported = selectedEntries.some(
		(e) => e.status === "exported",
	);
	const selectedDeletedCount = selectedEntries.filter(
		(e) => e.status === "deleted",
	).length;
	const selectionHasDeleted = selectedDeletedCount > 0;
	const sourceSelectionHasLockedParent = expandedSelectedParents.some(
		(entry) => entry.status === "exported" || entry.status === "deleted",
	);
	const hasMixedSelection =
		selectedCount > 0 || selectedLooseMiniIds.length > 0;
	const selectionSummary = useMemo(() => {
		const parts: string[] = [];
		if (selectedCount > 0) {
			parts.push(
				`${selectedCount} ${selectedCount === 1 ? "group" : "groups"}`,
			);
		}
		if (selectedLooseMiniIds.length > 0) {
			parts.push(
				`${selectedLooseMiniIds.length} ${
					selectedLooseMiniIds.length === 1 ? "entry" : "entries"
				}`,
			);
		}
		return parts.length > 0 ? `${parts.join(", ")} selected` : "";
	}, [selectedCount, selectedLooseMiniIds.length]);
	const [pendingAutoCombineIds, setPendingAutoCombineIds] = useState<
		Set<string>
	>(() => new Set());
	const [regeneratingNarrativeIds, setRegeneratingNarrativeIds] = useState<
		Set<string>
	>(() => new Set());
	const [streamingNarratives, setStreamingNarratives] = useState<
		Record<string, string>
	>({});
	const narrativeStreamTimersRef = useRef(
		new Map<string, NarrativeStreamTimer>(),
	);
	const generatedDialogNarrativesRef = useRef(new Map<string, string>());
	const acceptedDialogNarrativeIdsRef = useRef(new Set<string>());
	const stopNarrativeTableStream = useCallback((entryId: string) => {
		const timer = narrativeStreamTimersRef.current.get(entryId);
		if (timer) {
			clearInterval(timer.intervalId);
			if (timer.cleanupTimeoutId) clearTimeout(timer.cleanupTimeoutId);
			narrativeStreamTimersRef.current.delete(entryId);
		}
		setStreamingNarratives((prev) => {
			if (!(entryId in prev)) return prev;
			const next = { ...prev };
			delete next[entryId];
			return next;
		});
	}, []);
	const startNarrativeTableStream = useCallback(
		(
			entryId: string,
			finalNarrative: string,
			{ clearOnComplete = true }: { clearOnComplete?: boolean } = {},
		) => {
			stopNarrativeTableStream(entryId);

			const tokens = finalNarrative.split(/(\s+)/);
			let index = 0;
			let accumulated = "";

			setStreamingNarratives((prev) => ({ ...prev, [entryId]: "" }));

			const intervalId = setInterval(() => {
				while (index < tokens.length) {
					accumulated += tokens[index];
					index++;
					if (tokens[index - 1]?.trim()) break;
				}

				setStreamingNarratives((prev) => ({
					...prev,
					[entryId]: accumulated,
				}));

				if (index >= tokens.length) {
					clearInterval(intervalId);
					const timer = narrativeStreamTimersRef.current.get(entryId);
					if (!timer) return;
					if (!timer.clearOnComplete) return;
					timer.cleanupTimeoutId = setTimeout(() => {
						narrativeStreamTimersRef.current.delete(entryId);
						setStreamingNarratives((prev) => {
							if (!(entryId in prev)) return prev;
							const next = { ...prev };
							delete next[entryId];
							return next;
						});
					}, NARRATIVE_STREAM_INTERVAL_MS);
				}
			}, NARRATIVE_STREAM_INTERVAL_MS);

			narrativeStreamTimersRef.current.set(entryId, {
				intervalId,
				clearOnComplete,
			});
		},
		[stopNarrativeTableStream],
	);
	useEffect(() => {
		return () => {
			for (const timer of narrativeStreamTimersRef.current.values()) {
				clearInterval(timer.intervalId);
				if (timer.cleanupTimeoutId) clearTimeout(timer.cleanupTimeoutId);
			}
			narrativeStreamTimersRef.current.clear();
		};
	}, []);
	const selectionHasPending = selectedEntries.some((entry) =>
		pendingAutoCombineIds.has(entry.id),
	);
	const looseMiniSelectionHasPending = selectedLooseMiniIds.some((id) =>
		pendingAutoCombineIds.has(id),
	);
	const sourceSelectionHasPending =
		looseMiniSelectionHasPending ||
		expandedSelectedMiniIds.some((id) => pendingAutoCombineIds.has(id)) ||
		[...expandedSelectedParentIds].some((id) => pendingAutoCombineIds.has(id));
	const hideMoveToNewGroupRow =
		hasMixedSelection && selectionCoversSingleParent;
	const moveToNewGroupDisabledReason = useMemo(() => {
		if (!hasMixedSelection) return null;
		if (selectedSourceEntries.length === 0)
			return "Selected entries are no longer available";
		if (selectedSourceOwnerCount > 1)
			return "Selection spans multiple timekeepers";
		if (sourceSelectionHasLockedParent)
			return "Exported or deleted groups can't be moved";
		if (sourceSelectionHasPending) return "Selection is already updating";
		return null;
	}, [
		hasMixedSelection,
		selectedSourceEntries.length,
		selectedSourceOwnerCount,
		sourceSelectionHasLockedParent,
		sourceSelectionHasPending,
	]);
	const {
		canUndo,
		undoLabel,
		isUndoing,
		isMutating,
		undo,
		invalidateExportedGroups,
		runEntryMutation,
	} = useEntriesUndo();

	// ── Query mutations + deps ─────────────────────────────────
	const autoCombineMutation = useMutation<
		unknown,
		unknown,
		PendingAutoCombineAction,
		PendingAutoCombineContext
	>({
		mutationFn: (action: PendingAutoCombineAction) =>
			runEntryMutation(action.execute),
		onMutate: (action) => {
			const rollback = action.optimistic?.() ?? null;
			setPendingAutoCombineIds((prev) => {
				const next = new Set(prev);
				for (const id of action.pendingIds ?? []) next.add(id);
				return next;
			});
			setRegeneratingNarrativeIds((prev) => {
				const next = new Set(prev);
				for (const id of action.narrativePendingIds ?? []) next.add(id);
				return next;
			});
			const toastId = action.toast?.loading
				? toast.loading(action.toast.loading)
				: undefined;
			return { action, toastId, rollback };
		},
		onError: async (err, _action, ctx) => {
			if (err instanceof EntryMutationOutcomeError) {
				await invalidateExportedGroups(err.exportedEntryGroupIds);
			}
			ctx?.rollback?.();
			ctx?.action.rollback?.();
			await ctx?.action.onError?.(err);
			const message =
				typeof ctx?.action.toast?.error === "function"
					? ctx.action.toast.error(err)
					: (ctx?.action.toast?.error ?? "Request failed");
			toast.error(message, { id: ctx?.toastId });
		},
		onSuccess: async (result, _action, ctx) => {
			const exportedEntryGroupIds =
				result &&
				typeof result === "object" &&
				"exportedEntryGroupIds" in result
					? ((result as { exportedEntryGroupIds?: string[] })
							.exportedEntryGroupIds ?? [])
					: [];
			await invalidateExportedGroups(exportedEntryGroupIds);
			await ctx?.action.onSuccess?.();
			if (ctx?.action.toast?.success) {
				toast.success(ctx.action.toast.success, { id: ctx.toastId });
			} else if (ctx?.toastId) {
				toast.dismiss(ctx.toastId);
			}
		},
		onSettled: (_data, _err, action) => {
			setPendingAutoCombineIds((prev) => {
				const next = new Set(prev);
				for (const id of action.pendingIds ?? []) next.delete(id);
				return next;
			});
			setRegeneratingNarrativeIds((prev) => {
				const next = new Set(prev);
				for (const id of action.narrativePendingIds ?? []) next.delete(id);
				return next;
			});
		},
	});
	const enqueue = useCallback(
		(
			action: QueuedAction,
			pendingIds: string[],
			narrativePendingIds: string[] = [],
		) =>
			autoCombineMutation.mutate({
				...action,
				pendingIds,
				narrativePendingIds,
			}),
		[autoCombineMutation],
	);
	const reconcileAffectedEntryGroups = useCallback(
		(data: unknown): boolean => {
			const body = data as
				| {
						affectedEntryGroups?: unknown[];
						affected_entry_groups?: unknown[];
						data?: unknown[];
				  }
				| undefined;
			const candidates = Array.isArray(body?.affectedEntryGroups)
				? body.affectedEntryGroups
				: Array.isArray(body?.affected_entry_groups)
					? body.affected_entry_groups
					: Array.isArray(body?.data)
						? body.data
						: [];
			const groups = candidates.filter((group): group is EntryGroupWire =>
				Boolean(
					group &&
						typeof group === "object" &&
						"id" in group &&
						typeof group.id === "string" &&
						group.id.startsWith("eg_") &&
						"status" in group,
				),
			);
			if (!groups.length) return false;

			const matterMap = new Map(
				matters.map((matter) => [matter.matter_id, matter]),
			);
			const applications = new Map(
				(entryFilters.applicationOptions ?? []).map((option) => [
					String(option.value),
					option.label,
				]),
			);
			const rows = groups.map((group) =>
				entryGroupToEntryGroupEntry(group, {
					matters: matterMap,
					applications,
				}),
			);
			const rowById = new Map(rows.map((row) => [row.id, row]));
			queryClient.setQueriesData<AutoCombineEntryV2[]>(
				{ queryKey: entryGroupsQueryKey },
				(prev) => {
					if (!prev) return prev;
					const seen = new Set<string>();
					const patched = prev.map((entry) => {
						const replacement = rowById.get(entry.id);
						if (!replacement) return entry;
						seen.add(entry.id);
						return replacement;
					});
					for (const row of rows) {
						if (!seen.has(row.id)) patched.unshift(row);
					}
					return patched;
				},
			);
			return true;
		},
		[entryFilters.applicationOptions, matters, queryClient],
	);
	const depsRef = useRef<AutoCombineActionDeps>({
		makeApiRequest: makeScopedApiRequest,
		updateAutoCombine: (updater) => {
			const snapshots = queryClient.getQueriesData<AutoCombineEntryV2[]>({
				queryKey: entryGroupsQueryKey,
			});
			queryClient.setQueriesData<AutoCombineEntryV2[]>(
				{ queryKey: entryGroupsQueryKey },
				(prev) => (prev ? updater(prev) : prev),
			);
			return () => {
				for (const [queryKey, value] of snapshots) {
					queryClient.setQueryData(queryKey, value);
				}
			};
		},
		refreshAutoCombine,
		refreshEntryGroups,
		getAutoCombine: () => data,
		reconcileAffectedEntryGroups,
	});
	depsRef.current = {
		makeApiRequest: makeScopedApiRequest,
		updateAutoCombine: (updater) => {
			const snapshots = queryClient.getQueriesData<AutoCombineEntryV2[]>({
				queryKey: entryGroupsQueryKey,
			});
			queryClient.setQueriesData<AutoCombineEntryV2[]>(
				{ queryKey: entryGroupsQueryKey },
				(prev) => (prev ? updater(prev) : prev),
			);
			return () => {
				for (const [queryKey, value] of snapshots) {
					queryClient.setQueryData(queryKey, value);
				}
			};
		},
		refreshAutoCombine,
		refreshEntryGroups,
		getAutoCombine: () => data,
		reconcileAffectedEntryGroups,
	};
	const deps = useRef<AutoCombineActionDeps>({
		makeApiRequest: (opts) => depsRef.current.makeApiRequest(opts),
		updateAutoCombine: (updater) => depsRef.current.updateAutoCombine(updater),
		refreshAutoCombine: () => depsRef.current.refreshAutoCombine(),
		refreshEntryGroups: () => depsRef.current.refreshEntryGroups(),
		getAutoCombine: () => depsRef.current.getAutoCombine(),
		reconcileAffectedEntryGroups: (value) =>
			depsRef.current.reconcileAffectedEntryGroups?.(value) ?? false,
	}).current;
	const narrativePendingIdsFor = useCallback(
		(ids: string[]) =>
			ids.filter((id) => {
				const entry = data.find((candidate) => candidate.id === id);
				return !entry || !hasManualNarrative(entry);
			}),
		[data],
	);

	const lookupMatter = useCallback(
		(matterId: string): EntryMatter | null => {
			const m = matters.find((x) => x.matter_id === matterId);
			if (!m) return null;
			return {
				id: m.matter_id,
				code: m.matter,
				description: m.description,
				client: m.client,
			};
		},
		[matters],
	);

	const resolveTaskCodeId = useCallback(
		(value: string | null | undefined, current?: AutoCombineEntryV2 | null) => {
			if (!value) return null;
			if (value === current?.codes?.task && current.codes.taskId) {
				return current.codes.taskId;
			}
			return (
				entryFilters.taskCode.find(
					(option) => option.code === value || option.value === value,
				)?.value ?? value
			);
		},
		[entryFilters.taskCode],
	);

	const resolveActivityCodeId = useCallback(
		(value: string | null | undefined, current?: AutoCombineEntryV2 | null) => {
			if (!value) return null;
			if (value === current?.codes?.activity && current.codes.activityId) {
				return current.codes.activityId;
			}
			return (
				entryFilters.activityCode.find(
					(option) => option.code === value || option.value === value,
				)?.value ?? value
			);
		},
		[entryFilters.activityCode],
	);

	// ── Entry-group toolbar actions ────────────────────────────
	const onExport = useCallback(() => {
		if (
			selectedCount === 0 ||
			selectionHasExported ||
			selectionHasDeleted ||
			selectionHasPending
		)
			return;
		const ids = selectedEntries.map((entry) => entry.id);
		enqueue(exportAutoCombineAction(ids, deps), ids);
		clearMixedSelection();
	}, [
		clearMixedSelection,
		deps,
		enqueue,
		selectedEntries,
		selectedCount,
		selectionHasExported,
		selectionHasDeleted,
		selectionHasPending,
	]);
	const onMarkStatus = useCallback(
		(target: "tracked" | "reviewed") => {
			if (
				selectedCount === 0 ||
				selectionHasExported ||
				selectionHasDeleted ||
				selectionHasPending
			)
				return;
			const ids = selectedEntries.map((e) => e.id);
			enqueue(markAutoCombineStatusAction(ids, target, deps), ids);
			clearMixedSelection();
		},
		[
			clearMixedSelection,
			deps,
			enqueue,
			selectedEntries,
			selectedCount,
			selectionHasExported,
			selectionHasDeleted,
			selectionHasPending,
		],
	);
	const onDelete = useCallback(() => {
		if (
			!hasMixedSelection ||
			selectionHasExported ||
			selectionHasDeleted ||
			selectionHasPending ||
			sourceSelectionHasLockedParent ||
			sourceSelectionHasPending
		)
			return;

		const groupIds = selectedEntries.map((e) => e.id);
		const sourceParentIds = [...selectedLooseMiniParentIds];
		const narrativePendingIds = narrativePendingIdsFor(sourceParentIds);
		enqueue(
			deleteAutoCombineSelectionAction(groupIds, selectedLooseMiniIds, deps),
			[
				...new Set([
					...groupIds,
					...selectedLooseMiniIds,
					...sourceParentIds,
					...narrativePendingIds,
				]),
			],
			narrativePendingIds,
		);
		clearMixedSelection();
	}, [
		clearMixedSelection,
		deps,
		enqueue,
		hasMixedSelection,
		narrativePendingIdsFor,
		selectedEntries,
		selectedLooseMiniIds,
		selectedLooseMiniParentIds,
		selectionHasExported,
		selectionHasDeleted,
		selectionHasPending,
		sourceSelectionHasLockedParent,
		sourceSelectionHasPending,
	]);
	const onRestore = useCallback(() => {
		const ids = selectedEntries
			.filter((e) => e.status === "deleted")
			.map((e) => e.id);
		if (ids.length === 0) return;
		if (ids.some((id) => pendingAutoCombineIds.has(id))) return;
		enqueue(restoreAutoCombineAction(ids, deps), ids);
		clearMixedSelection();
	}, [
		clearMixedSelection,
		deps,
		enqueue,
		pendingAutoCombineIds,
		selectedEntries,
	]);
	const onUndo = useCallback(async () => {
		clearMixedSelection();
		await undo();
		clearMixedSelection();
	}, [clearMixedSelection, undo]);

	// ── Source-entry actions ──────────────────────────────
	const moveMode = useMemo<MoveMode>(
		() => ({
			active:
				expandedSelectedMiniIds.length > 0 &&
				!sourceSelectionHasLockedParent &&
				!sourceSelectionHasPending,
			isValidTarget: (entry) =>
				(entry.status === "tracked" || entry.status === "reviewed") &&
				!pendingAutoCombineIds.has(entry.id) &&
				expandedSelectedMiniIds.some(
					(id) => !entry.miniTransactions.some((mini) => mini.id === id),
				),
			onMove: (entry) => {
				if (sourceSelectionHasLockedParent || sourceSelectionHasPending) return;
				const targetMiniIds = new Set(
					entry.miniTransactions.map((mini) => mini.id),
				);
				const ids = expandedSelectedMiniIds.filter(
					(id) => !targetMiniIds.has(id),
				);
				if (ids.length === 0) return;
				const sourceParentIds = [
					...new Set(
						ids
							.map((id) => miniParentById.get(id)?.id)
							.filter((id): id is string => Boolean(id)),
					),
				];
				const narrativeIds = [...new Set([...sourceParentIds, entry.id])];
				const narrativePendingIds = narrativePendingIdsFor(narrativeIds);
				enqueue(
					moveMiniTransactionsAction(ids, entry.id, deps),
					[...new Set([...ids, ...sourceParentIds, ...narrativePendingIds])],
					narrativePendingIds,
				);
				clearMixedSelection();
			},
		}),
		[
			clearMixedSelection,
			deps,
			enqueue,
			expandedSelectedMiniIds,
			miniParentById,
			narrativePendingIdsFor,
			pendingAutoCombineIds,
			sourceSelectionHasLockedParent,
			sourceSelectionHasPending,
		],
	);

	const onMoveSelectionToNewGroup = useCallback(() => {
		if (moveToNewGroupDisabledReason || selectedSourceEntries.length === 0)
			return;
		const sourceIds = uniqueSelectedSourceIds(selectedSourceEntries);
		if (sourceIds.length === 0) return;
		const sourceParentIds = uniqueSelectedParents(selectedSourceEntries).map(
			(parent) => parent.id,
		);
		const narrativePendingIds = narrativePendingIdsFor(sourceParentIds);
		enqueue(
			createEntryGroupFromSourceEntriesAction(
				sourceIds,
				resolveNewEntryGroupFields(selectedSourceEntries),
				deps,
			),
			[...new Set([...sourceIds, ...sourceParentIds, ...narrativePendingIds])],
			narrativePendingIds,
		);
		clearMixedSelection();
	}, [
		clearMixedSelection,
		deps,
		enqueue,
		moveToNewGroupDisabledReason,
		narrativePendingIdsFor,
		selectedSourceEntries,
	]);

	// ── Add / Edit dialogs backed by entry-group endpoints ─
	const [addOpen, setAddOpen] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState<AutoCombineEntryV2 | null>(
		null,
	);
	const [selectedMiniEntry, setSelectedMiniEntry] = useState<{
		parent: AutoCombineEntryV2;
		mini: EntryMiniTransaction;
	} | null>(null);
	const applyAdd = useCallback(
		(
			values: {
				date: Date;
				matter_id: string;
				task: string;
				activity: string;
				duration: number;
				description: string;
			},
			shouldExport = false,
		) => {
			const matter = values.matter_id ? lookupMatter(values.matter_id) : null;
			const dateStr = format(values.date, "yyyy-MM-dd");
			const durationSeconds = Math.round(values.duration * 3600);
			// Stamp the current local time-of-day (pinned to UTC like every entry) so
			// a manual add reads the time it was added, not 12:00 AM.
			const nowLocal = new Date();
			const pad = (n: number) => String(n).padStart(2, "0");
			const timeOfDay = `${pad(nowLocal.getHours())}:${pad(nowLocal.getMinutes())}:${pad(nowLocal.getSeconds())}`;
			const startTime = `${dateStr}T${timeOfDay}.000Z`;
			const endTime = new Date(
				new Date(startTime).getTime() + durationSeconds * 1000,
			).toISOString();
			enqueue(
				addAutoCombineEntryGroupAction(
					{
						matterId: values.matter_id || undefined,
						clientId: matter?.client ?? undefined,
						taskCodeId: resolveTaskCodeId(values.task) || undefined,
						activityCodeId: resolveActivityCodeId(values.activity) || undefined,
						narrative: values.description,
						startTime,
						endTime,
						durationSeconds,
						shouldExport,
					},
					deps,
					{ onSuccess: () => setAddOpen(false) },
				),
				[],
			);
		},
		[deps, enqueue, lookupMatter, resolveActivityCodeId, resolveTaskCodeId],
	);

	const applyEdit = useCallback(
		(
			entryId: string,
			values: {
				date: Date;
				matter_id: string;
				task: string;
				activity: string;
				duration: number;
				description: string;
			},
			nextStatusTarget?: EntryStatus,
		) => {
			const current = data.find((entry) => entry.id === entryId) ?? null;
			const originalDescription =
				selectedEntry?.id === entryId
					? selectedEntry.description
					: current?.description;
			const narrativeChanged =
				originalDescription !== undefined
					? values.description !== originalDescription
					: true;
			const nextMatterId = values.matter_id || null;
			const nextTaskCodeId = resolveTaskCodeId(values.task, current);
			const nextActivityCodeId = resolveActivityCodeId(
				values.activity,
				current,
			);
			const durationSeconds = durationToUnitsSeconds(values.duration);
			const changedStatus =
				nextStatusTarget !== undefined && nextStatusTarget !== current?.status
					? nextStatusTarget
					: undefined;
			const shouldStreamSavedNarrative =
				narrativeChanged && generatedDialogNarrativesRef.current.has(entryId);
			const matterChanged = current
				? nextMatterId !== (current.matter?.id ?? null)
				: true;
			const currentTaskCodeId = current?.codes?.taskId ?? null;
			const currentActivityCodeId = current?.codes?.activityId ?? null;
			const taskChanged = current
				? (values.task || null) !== (current.codes?.task ?? null) ||
					(currentTaskCodeId !== null && nextTaskCodeId !== currentTaskCodeId)
				: true;
			const activityChanged = current
				? (values.activity || null) !== (current.codes?.activity ?? null) ||
					(currentActivityCodeId !== null &&
						nextActivityCodeId !== currentActivityCodeId)
				: true;
			// Compare against the units the dialog opened with, the same way the
			// narrative does above. Converting the form's units back to seconds and
			// comparing against the group's measured seconds reported a change on
			// every save: a timer that ran 10s shows 0.1 units, which converts to
			// 360s. That sent a phantom duration override and flipped
			// is_duration_dirty, so the group displayed 6m instead of its real 10s.
			// `current` is re-read from the cache, and a running timer's units move
			// underneath it, so the seeded value is what the user actually saw.
			const originalDuration =
				selectedEntry?.id === entryId ? selectedEntry.units : current?.units;
			const durationChanged =
				originalDuration !== undefined
					? values.duration !== originalDuration
					: true;
			if (
				!narrativeChanged &&
				!matterChanged &&
				!taskChanged &&
				!activityChanged &&
				!durationChanged &&
				!changedStatus
			) {
				return;
			}
			const action = editAutoCombineEntryGroupAction(
				entryId,
				{
					...(matterChanged
						? {
								matterId: nextMatterId,
								matter: nextMatterId ? lookupMatter(nextMatterId) : null,
							}
						: {}),
					...(taskChanged
						? {
								taskCodeId: nextTaskCodeId,
								taskCodeDisplay: values.task || null,
							}
						: {}),
					...(activityChanged
						? {
								activityCodeId: nextActivityCodeId,
								activityCodeDisplay: values.activity || null,
							}
						: {}),
					...(durationChanged ? { durationSeconds } : {}),
					...(narrativeChanged ? { narrative: values.description } : {}),
					...(changedStatus ? { status: changedStatus } : {}),
				},
				deps,
			);
			if (shouldStreamSavedNarrative) {
				const originalOnError = action.onError;
				action.onError = async (err) => {
					stopNarrativeTableStream(entryId);
					await originalOnError?.(err);
				};
			}
			enqueue(action, [entryId]);
			if (shouldStreamSavedNarrative) {
				acceptedDialogNarrativeIdsRef.current.add(entryId);
				startNarrativeTableStream(entryId, values.description);
				generatedDialogNarrativesRef.current.delete(entryId);
			}
		},
		[
			data,
			deps,
			enqueue,
			lookupMatter,
			resolveActivityCodeId,
			resolveTaskCodeId,
			selectedEntry,
			stopNarrativeTableStream,
			startNarrativeTableStream,
		],
	);

	const applyMiniEdit = useCallback(
		(
			entryId: string,
			values: {
				duration: number;
				time: string;
				description: string;
			},
		) => {
			const parentId = selectedMiniEntry?.parent.id;
			const narrativePendingIds = parentId
				? narrativePendingIdsFor([parentId])
				: [];
			// Only send the duration when the attorney actually changed it. The
			// dialog is seeded from `units`, so a 3m30s fill opens showing 0.1 and
			// converting that back gives 360s. Sending it on a description-only save
			// overwrote the fill's measured time, and the next resume banked the
			// wrong total. A real edit still sends, and is still banked on resume.
			const originalDuration = selectedMiniEntry?.mini.units;
			const durationChanged =
				originalDuration === undefined || values.duration !== originalDuration;
			enqueue(
				editAutoCombineSourceEntryAction(
					entryId,
					{
						...(durationChanged
							? { unitsSeconds: durationToUnitsSeconds(values.duration) }
							: {}),
						time: values.time,
						description: values.description,
					},
					deps,
				),
				[entryId, ...narrativePendingIds],
				narrativePendingIds,
			);
		},
		[
			deps,
			enqueue,
			narrativePendingIdsFor,
			selectedMiniEntry?.parent.id,
			selectedMiniEntry?.mini.units,
		],
	);

	const closeEdit = useCallback(() => {
		setSelectedEntry((entry) => {
			if (entry) {
				if (acceptedDialogNarrativeIdsRef.current.has(entry.id)) {
					acceptedDialogNarrativeIdsRef.current.delete(entry.id);
				} else {
					stopNarrativeTableStream(entry.id);
				}
				generatedDialogNarrativesRef.current.delete(entry.id);
			}
			return null;
		});
	}, [stopNarrativeTableStream]);
	const applyDuplicate = useCallback(
		(entryId: string) => {
			enqueue(duplicateAutoCombineEntryGroupAction(entryId, deps), [entryId]);
		},
		[deps, enqueue],
	);
	const resetSelectedEntryDuration = useCallback(() => {
		if (!selectedEntry || pendingAutoCombineIds.has(selectedEntry.id)) return;
		enqueue(
			editAutoCombineEntryGroupAction(
				selectedEntry.id,
				{ clearDurationOverride: true },
				deps,
			),
			[selectedEntry.id],
		);
		closeEdit();
	}, [closeEdit, deps, enqueue, pendingAutoCombineIds, selectedEntry]);
	const closeMiniEdit = useCallback(() => setSelectedMiniEntry(null), []);
	const onRowClick = useCallback(
		(entry: AutoCombineEntryV2) => {
			if (pendingAutoCombineIds.has(entry.id)) return;
			setSelectedEntry(entry);
		},
		[pendingAutoCombineIds],
	);
	const onMiniRowClick = useCallback(
		(parent: AutoCombineEntryV2, mini: EntryMiniTransaction) => {
			if (
				pendingAutoCombineIds.has(parent.id) ||
				pendingAutoCombineIds.has(mini.id)
			)
				return;
			clearMixedSelection();
			setSelectedMiniEntry({ parent, mini });
		},
		[pendingAutoCombineIds, clearMixedSelection],
	);

	// ── Derived UI ────────────────────────────────────────────
	const statsEntries = useMemo(() => {
		const predicate = makeEntryPredicate(
			{ status: ["tracked", "reviewed", "exported", "deleted"] },
			dateFilter,
		);
		return data.filter(predicate);
	}, [data, dateFilter]);
	const selectedStatsEntries = useMemo(() => {
		if (!hasMixedSelection) return [];
		const looseMiniEntries = selectedLooseMiniIds
			.map((id) => {
				const parent = miniParentById.get(id);
				const mini = parent?.miniTransactions.find((item) => item.id === id);
				if (!parent || !mini) return null;
				return {
					...parent,
					id: mini.id,
					date: mini.date || parent.date,
					units: mini.units,
					unitsSeconds: mini.unitsSeconds ?? null,
					startTime: mini.time,
					endTime: mini.time,
				};
			})
			.filter((entry): entry is AutoCombineEntryV2 => Boolean(entry));
		return [...selectedEntries, ...looseMiniEntries];
	}, [
		hasMixedSelection,
		miniParentById,
		selectedEntries,
		selectedLooseMiniIds,
	]);

	const hasActiveFilters = !isDefaultFilters(filters, defaultFilters);
	const selectedDateRange = isCompleteDateRange(dateRange)
		? dateRange
		: { from: date, to: date };

	const lockedByStatus = selectionHasExported || selectionHasDeleted;
	const statusLockReason = selectionHasDeleted ? "deleted" : "exported";
	const sourceLockReason = expandedSelectedParents.some(
		(entry) => entry.status === "deleted",
	)
		? "deleted"
		: "exported";
	const deleteDisabled =
		!hasMixedSelection ||
		lockedByStatus ||
		selectionHasPending ||
		sourceSelectionHasLockedParent ||
		sourceSelectionHasPending;
	const deleteLabel =
		lockedByStatus || sourceSelectionHasLockedParent
			? `Delete (selection includes ${
					lockedByStatus ? statusLockReason : sourceLockReason
				})`
			: "Delete";

	const filterMenuSharedProps = {
		fieldOptions,
		filters,
		defaultFilters,
		onToggleFieldValue: toggleFilterFieldValue,
		onSelectAll: selectAllFilterValues,
		onSelectNone: selectNoFilterValues,
		onReset: resetFilters,
	};

	const editLegacyEntry = selectedEntry
		? ({
				id: Number(selectedEntry.id.replace(/^eg_/, "")) || 0,
				date: selectedEntry.date,
				matter_id: selectedEntry.matter?.id ?? "",
				matter: selectedEntry.matter?.code ?? "",
				task: selectedEntry.codes?.task ?? "",
				activity: selectedEntry.codes?.activity ?? "",
				duration: selectedEntry.units,
				description: selectedEntry.description,
				document_name: undefined,
				attorney_feedback: null,
			} as const)
		: null;
	const dialogNarrativeEntry = selectedEntry
		? (data.find((entry) => entry.id === selectedEntry.id) ?? selectedEntry)
		: null;
	const dialogNarrativeCanRegenerate = Boolean(
		dialogNarrativeEntry &&
			(dialogNarrativeEntry.status === "tracked" ||
				dialogNarrativeEntry.status === "reviewed"),
	);
	const regenerateDialogNarrative = useCallback(async (): Promise<string> => {
		if (!dialogNarrativeEntry || !dialogNarrativeCanRegenerate) {
			throw new Error("Narrative regeneration is not available");
		}

		const entryId = dialogNarrativeEntry.id;
		setPendingAutoCombineIds((prev) => new Set(prev).add(entryId));
		setRegeneratingNarrativeIds((prev) => new Set(prev).add(entryId));

		try {
			const resp = await runEntryMutation(() =>
				makeApiRequest(
					withActingHeaders(
						{
							endpoint: `/v2/entry-groups/${entryId}/regenerate-narrative`,
							method: "POST",
							body: { mode: "preview" },
						},
						dialogNarrativeEntry.timekeeperId,
					),
				),
			);
			if (resp.status === "ERROR") {
				throw new Error(resp.error || "Failed to regenerate narrative");
			}
			const updated = firstEntryGroupResponse(resp.data, entryId);
			if (typeof updated?.predictedNarrative === "string") {
				generatedDialogNarrativesRef.current.set(
					entryId,
					updated.predictedNarrative,
				);
				startNarrativeTableStream(entryId, updated.predictedNarrative, {
					clearOnComplete: false,
				});
				return updated.predictedNarrative;
			}

			await refreshAutoCombine();
			const refreshed = queryClient
				.getQueryData<AutoCombineEntryV2[]>(entriesAutoCombineQueryKey)
				?.find((entry) => entry.id === entryId);
			if (typeof refreshed?.predictedNarrative === "string") {
				generatedDialogNarrativesRef.current.set(
					entryId,
					refreshed.predictedNarrative,
				);
				startNarrativeTableStream(entryId, refreshed.predictedNarrative, {
					clearOnComplete: false,
				});
				return refreshed.predictedNarrative;
			}

			throw new Error("Regenerated narrative was not returned");
		} finally {
			setPendingAutoCombineIds((prev) => {
				const next = new Set(prev);
				next.delete(entryId);
				return next;
			});
			setRegeneratingNarrativeIds((prev) => {
				const next = new Set(prev);
				next.delete(entryId);
				return next;
			});
		}
	}, [
		dialogNarrativeCanRegenerate,
		dialogNarrativeEntry,
		makeApiRequest,
		queryClient,
		refreshAutoCombine,
		runEntryMutation,
		startNarrativeTableStream,
	]);

	return (
		<div className="flex h-full w-full flex-col">
			<TooltipProvider delayDuration={300}>
				<div className="bg-background px-4 pt-4 pb-2">
					<div className={styles.toolbarContainer}>
						<div className={styles.toolbar}>
							{/* Left: workflow actions for mixed group/source selections. */}
							<div className={styles.bulkActions}>
								<ButtonGroup>
									<IconButton
										iconNode={<Undo2 />}
										label={undoLabel ? `Undo ${undoLabel}` : "Undo"}
										onClick={() => void onUndo()}
										disabled={!canUndo || isUndoing || isMutating}
									/>
								</ButtonGroup>
								<ButtonGroup>
									<IconButton
										iconNode={<StatusIcon status="tracked" />}
										label={
											lockedByStatus
												? `Mark tracked (selection includes ${statusLockReason})`
												: "Mark tracked"
										}
										onClick={() => onMarkStatus("tracked")}
										disabled={
											selectedCount === 0 ||
											lockedByStatus ||
											selectionHasPending
										}
									/>
									<IconButton
										iconNode={<StatusIcon status="reviewed" />}
										label={
											lockedByStatus
												? `Mark reviewed (selection includes ${statusLockReason})`
												: "Mark reviewed"
										}
										onClick={() => onMarkStatus("reviewed")}
										disabled={
											selectedCount === 0 ||
											lockedByStatus ||
											selectionHasPending
										}
									/>
									<IconButton
										iconNode={<Trash2 className="text-destructive" />}
										label={deleteLabel}
										onClick={onDelete}
										disabled={deleteDisabled}
									/>
								</ButtonGroup>
								{hasMixedSelection && (
									<div className="flex items-center gap-0.5 pl-1 animate-in fade-in-0 slide-in-from-left-2 duration-150">
										<span className="text-sm font-medium tabular-nums">
											{selectionSummary}
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="size-7 text-muted-foreground"
											onClick={clearMixedSelection}
											aria-label="Clear selection"
										>
											<X />
										</Button>
									</div>
								)}
							</div>

							{/* Center: filters / date / search. */}
							<div className={styles.centerActions}>
								<FilterMenu
									{...filterMenuSharedProps}
									trigger={
										<Button
											variant="outline"
											size="icon"
											aria-label="Filter"
											className={cn(
												"relative",
												hasActiveFilters &&
													"border-ring text-ring hover:border-ring hover:text-ring",
											)}
										>
											<Filter />
											{hasActiveFilters && (
												<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
											)}
										</Button>
									}
								/>
								<DatePicker
									className="w-fit"
									dateRange={selectedDateRange}
									onDateChange={setDate}
									onDateRangeChange={setDateRange}
									selectionMode="range"
									bottomSlot={DatePickerDefaultFooter}
								/>
								<SearchBar
									value={searchQuery}
									onChange={setSearchQuery}
									onClear={() => setSearchQuery("")}
								/>
							</div>

							{/* Right: matter lock, Add, and the primary action. */}
							<div className={styles.rightActions}>
								<MatterLockButton
									matters={matters}
									value={settings.hardcoded_matter}
									onChange={(id) => updateSettings({ hardcoded_matter: id })}
								/>
								<ToolbarTimerButton />
								<TooltipButton
									variant="outline"
									aria-label="Add"
									onClick={() => setAddOpen(true)}
									tooltip="Add"
								>
									<Plus />
									Add
								</TooltipButton>
								{selectionHasDeleted ? (
									<TooltipButton
										variant="default"
										aria-label="Restore"
										onClick={onRestore}
										className="min-w-28 border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
										tooltip={`${selectedDeletedCount} deleted selected`}
									>
										<RotateCcw />
										Restore
									</TooltipButton>
								) : (
									<TooltipButton
										variant="default"
										aria-label="Export"
										onClick={onExport}
										disabled={
											selectedCount === 0 ||
											selectionHasExported ||
											selectionHasDeleted ||
											selectionHasPending
										}
										className="min-w-28 bg-success text-success-foreground shadow-success/25 hover:bg-success/90"
										tooltip="Export selected entries"
									>
										<Send />
										Export
									</TooltipButton>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="min-h-0 flex-1 px-4 pb-4 pt-1">
					<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
						<div className="relative min-h-0 flex-1">
							<AutoCombineTable
								table={table}
								rowSelection={rowSelection}
								isLoading={isLoading}
								expandedIds={expandedIds}
								onToggleExpand={toggleExpand}
								onExpandAll={expandAll}
								onCollapseAll={collapseAll}
								selectedMiniIds={selectedMiniIds}
								selectedGroupIds={selectedGroupIds}
								onToggleMini={handleToggleMini}
								onEditMini={onMiniRowClick}
								onRowClick={onRowClick}
								moveMode={moveMode}
								newGroupAction={
									hasMixedSelection && !hideMoveToNewGroupRow
										? {
												disabledReason: moveToNewGroupDisabledReason,
												onClick: onMoveSelectionToNewGroup,
											}
										: null
								}
								pendingIds={pendingAutoCombineIds}
								regeneratingNarrativeIds={regeneratingNarrativeIds}
								streamingNarratives={streamingNarratives}
							/>
							<div className="absolute right-0 top-0 z-20 flex h-10 w-7 items-center justify-center bg-muted">
								<ColumnsMenu
									columnVisibility={effectiveColumnVisibility}
									onColumnVisibilityChange={setColumnVisibility}
									columnOrder={columnOrder}
									onColumnOrderChange={setColumnOrder}
									labels={columnMenuLabels}
									defaultOrder={defaultColumnOrder}
									defaultVisibility={AUTO_DEFAULT_COLUMN_VISIBILITY}
								/>
							</div>
						</div>
						<StatsBar
							entries={
								hasMixedSelection && selectedStatsEntries.length > 0
									? selectedStatsEntries
									: statsEntries
							}
							selectionActive={hasMixedSelection}
						/>
					</div>
				</div>
			</TooltipProvider>

			<AddEntryDialog
				open={addOpen}
				title="Add Entry Group"
				date={date}
				timerPrefillDuration={null}
				onOpenChange={setAddOpen}
				onSubmit={(values) =>
					applyAdd({
						date: values.date,
						matter_id: values.matter_id,
						task: values.task,
						activity: values.activity ?? "",
						duration: values.duration,
						description: values.description,
					})
				}
				onExport={(values) =>
					applyAdd(
						{
							date: values.date,
							matter_id: values.matter_id,
							task: values.task,
							activity: values.activity ?? "",
							duration: values.duration,
							description: values.description,
						},
						true,
					)
				}
			/>
			<EditEntryDialog
				entry={editLegacyEntry}
				title="Edit Entry Group"
				failedExports={EMPTY_FAILED_EXPORTS}
				status={selectedEntry?.status}
				onRegenerateNarrative={
					dialogNarrativeCanRegenerate ? regenerateDialogNarrative : undefined
				}
				activeTimerReadOnlyNote={
					!!runningId && selectedEntry?.id === runningId
						? "Pause the timer to edit"
						: undefined
				}
				unitsActionSlot={
					selectedEntry?.isDurationDirty &&
					selectedEntry.status !== "deleted" &&
					selectedEntry.status !== "exported" &&
					!(runningId && selectedEntry.id === runningId) ? (
						<TooltipButton
							type="button"
							variant="ghost"
							size="sm"
							className="h-7 px-2"
							aria-label="Reset units"
							tooltip="Reset units to calculated"
							disabled={pendingAutoCombineIds.has(selectedEntry.id)}
							onClick={resetSelectedEntryDuration}
						>
							<RotateCcw className="size-3.5" />
							Reset
						</TooltipButton>
					) : null
				}
				narrativeSlot={
					dialogNarrativeEntry ? (
						<NarrativeProvenanceChip entry={dialogNarrativeEntry} />
					) : null
				}
				onOpenChange={(open) => {
					if (!open) closeEdit();
				}}
				onResolveFeedback={() => undefined}
				onSubmit={(_entry, values, status) => {
					if (selectedEntry) applyEdit(selectedEntry.id, values, status);
					closeEdit();
				}}
				onExport={(_entry, values) => {
					if (selectedEntry) {
						applyEdit(selectedEntry.id, values);
						enqueue(exportAutoCombineAction([selectedEntry.id], deps), [
							selectedEntry.id,
						]);
					}
					closeEdit();
				}}
				onDuplicate={() => {
					if (selectedEntry) applyDuplicate(selectedEntry.id);
					closeEdit();
				}}
				onDelete={() => {
					if (selectedEntry)
						enqueue(deleteAutoCombineAction([selectedEntry.id], deps), [
							selectedEntry.id,
						]);
					closeEdit();
				}}
				onRestore={() => {
					if (selectedEntry)
						enqueue(restoreAutoCombineAction([selectedEntry.id], deps), [
							selectedEntry.id,
						]);
					closeEdit();
				}}
			/>
			<SourceEntryEditDialog
				entry={selectedMiniEntry?.mini ?? null}
				matterId={selectedMiniEntry?.parent.matter?.id}
				matterClient={selectedMiniEntry?.parent.matter?.client}
				activeTimerReadOnlyNote={
					!!runningId && selectedMiniEntry?.parent.id === runningId
						? "Pause the timer to edit"
						: undefined
				}
				onOpenChange={(open) => {
					if (!open) closeMiniEdit();
				}}
				onSubmit={(entry, values) => {
					applyMiniEdit(entry.id, values);
					closeMiniEdit();
				}}
			/>
		</div>
	);
}

const EMPTY_FAILED_EXPORTS: ReadonlyMap<number, string> = new Map();

function NarrativeProvenanceChip({ entry }: { entry: AutoCombineEntryV2 }) {
	const stale = hasStaleNarrative(entry);
	const manual = hasManualNarrative(entry);

	if (!stale && !manual) return null;

	const label = stale
		? manual
			? "Manual narrative; source changed"
			: "Narrative out of date"
		: "Manual narrative";
	const variant = stale ? (manual ? "destructive" : "warning") : "muted";
	const details = [
		entry.narrativeSourceDirtyAt
			? `Source changed ${formatNarrativeTimestamp(entry.narrativeSourceDirtyAt)}`
			: null,
		entry.narrativeEditedAt
			? `Manual edit ${formatNarrativeTimestamp(entry.narrativeEditedAt)}`
			: null,
		entry.narrativeGeneratedAt
			? `Generated ${formatNarrativeTimestamp(entry.narrativeGeneratedAt)}`
			: null,
		entry.narrativeSourceEntryIds.length > 0
			? `${entry.narrativeSourceEntryIds.length} source ${entry.narrativeSourceEntryIds.length === 1 ? "entry" : "entries"}`
			: null,
		entry.predictedNarrative ? "Generated preview available" : null,
	].filter((detail): detail is string => Boolean(detail));
	const title = details.length > 0 ? details.join(" · ") : undefined;

	return (
		<div className="flex flex-wrap items-center gap-1.5" title={title}>
			<Badge variant={variant} className="gap-1">
				{stale ? <AlertTriangle className="size-3" /> : null}
				{label}
			</Badge>
		</div>
	);
}

function formatNarrativeTimestamp(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString(undefined, {
		dateStyle: "medium",
		timeStyle: "short",
	});
}
